import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Search, TrendingUp, TrendingDown, Plus, Minus, Target } from 'lucide-react';

const keywordRules = [
  { label: 'Auto-promote winners', desc: 'Search terms with 3+ orders and <20% ACOS' },
  { label: 'Auto-negative losers', desc: 'Search terms with 15+ clicks and 0 orders' },
  { label: 'Bid adjustments', desc: 'Increase bids 10% for keywords with ROAS >4x' },
];

export default function KeywordOptimizationSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.2 });

  return (
    <section
      ref={sectionRef}
      className="relative bg-[#05060B] grid-bg py-24 lg:py-32 overflow-hidden"
    >
      <div className="relative z-10 w-full px-6 lg:px-12 xl:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div 
              className={`order-2 lg:order-1 transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}
            >
              <div className="glass-card p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                      <Search className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <div className="font-medium text-white">Keyword Performance</div>
                      <div className="text-sm text-dark-500">Last 30 days</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  {[
                    { keyword: 'kitchen organizer', orders: 47, acos: 18.2, trend: 'up' },
                    { keyword: 'storage bins', orders: 32, acos: 22.5, trend: 'down' },
                    { keyword: 'cabinet shelf', orders: 28, acos: 15.8, trend: 'up' },
                    { keyword: 'pantry containers', orders: 0, acos: 0, clicks: 23, action: 'negative' },
                  ].map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 rounded-xl bg-dark-900/30 border border-cyan-500/10"
                    >
                      <div className="flex-1">
                        <div className="text-sm text-white">{item.keyword}</div>
                        <div className="text-xs text-dark-500">
                          {item.orders > 0 ? `${item.orders} orders` : `${item.clicks} clicks, 0 orders`}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {item.orders > 0 ? (
                          <>
                            <span className="text-sm text-cyan-400">{item.acos}% ACOS</span>
                            {item.trend === 'up' ? (
                              <TrendingUp className="w-4 h-4 text-green-400" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-yellow-400" />
                            )}
                          </>
                        ) : (
                          <span className="flex items-center gap-2 px-2 py-1 rounded bg-red-500/10 text-red-400 text-xs">
                            <Minus className="w-3 h-3" />
                            Add Negative
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 text-sm hover:bg-green-500/20 transition-colors">
                    <Plus className="w-4 h-4" />
                    Promote 3 Winners
                  </button>
                  <button className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm hover:bg-red-500/20 transition-colors">
                    <Minus className="w-4 h-4" />
                    Negative 5 Losers
                  </button>
                </div>
              </div>
            </div>

            <div 
              className={`order-1 lg:order-2 transition-all duration-1000 delay-200 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium uppercase tracking-wider mb-6">
                Keyword Optimization
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                Smarter keyword{' '}
                <span className="text-cyan-400 cyan-glow">management.</span>
              </h2>
              <p className="text-lg text-dark-400 mb-8">
                Automatically identify winning search terms to promote and underperformers to negate. Our AI continuously optimizes your keyword portfolio.
              </p>

              <div className="space-y-4">
                {keywordRules.map((rule, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4 p-4 rounded-xl bg-dark-900/30 border border-cyan-500/10 hover:border-cyan-500/30 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                      <Target className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white mb-1">{rule.label}</h4>
                      <p className="text-sm text-dark-400">{rule.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
